(function () {
  'use strict';

  const STORAGE_KEY = 'mrparquet:project-quote:v1';
  const VERSION = 1;
  const FIXED_WASTE_PERCENT = 10;
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const root = document.getElementById('ProjectQuoteRoot');
  if (!root) return;

  const builderUrl = root.dataset.builderUrl || '/pages/cotizador?view=cotizador';
  const shopDomain = root.dataset.shopDomain || window.Shopify?.shop || '';
  const defaultCurrency = root.dataset.currency || 'USD';
  const live = document.getElementById('ProjectQuoteLive');
  let activeOverlay = null;
  let returnFocus = null;
  let editingItemId = null;
  let activeProduct = null;

  const uid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `pq-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };
  const nowIso = () => new Date().toISOString();
  const number = (value, fallback = 0) => {
    const parsed = Number(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const normalizedWaste = (value) => Math.min(100, Math.max(0, value == null ? FIXED_WASTE_PERCENT : number(value)));
  const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
  const displayDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value || '');
  };
  const parseDisplayDate = (value) => {
    const match = String(value || '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
    return `${match[3]}-${match[2]}-${match[1]}`;
  };
  const formatDateTyping = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/');
  };
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const variantIsAvailable = (variant) => Boolean(variant) && variant.available !== false;

  function requireAvailableVariant(variant) {
    if (!variant) throw new Error('Elegí una variante antes de agregar el producto.');
    if (!variantIsAvailable(variant)) throw new Error('La variante seleccionada no está disponible. Elegí otra opción.');
    return variant;
  }

  function storedItemVariantIsAvailable(item) {
    const variants = Array.isArray(item?.availableVariants) ? item.availableVariants : [];
    // Entries saved before availability was persisted remain valid for backwards compatibility.
    if (!variants.length) return true;
    const selected = variants.find((variant) => String(variant.id) === String(item.variantId));
    return variantIsAvailable(selected);
  }

  function freshState() {
    const createdAt = nowIso();
    return {
      version: VERSION,
      consentDefaultsVersion: 1,
      id: uid(),
      createdAt,
      updatedAt: createdAt,
      expiresAt: new Date(Date.now() + MAX_AGE_MS).toISOString(),
      currency: defaultCurrency,
      items: [],
      project: {
        name: '', kind: 'residential', workType: 'renovation', location: '',
        rooms: [{ id: uid(), name: 'Ambiente', areaSqm: null }],
        desiredDate: '', notes: ''
      },
      services: {},
      contact: {
        fullName: '', email: '', phone: '', company: '', preferredChannel: 'whatsapp',
        comments: '', processingConsent: true, marketingConsent: true
      },
      documents: [],
      attribution: captureAttribution(),
      submission: { status: 'idle', idempotencyKey: uid(), reference: null, error: null }
    };
  }

  function captureAttribution() {
    const query = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'].forEach((key) => {
      const value = query.get(key);
      if (value) utm[key] = value.slice(0, 250);
    });
    return {
      landingPage: `${window.location.pathname}${window.location.search}`.slice(0, 1000),
      referrer: String(document.referrer || '').slice(0, 1000),
      utm
    };
  }

  function normalizeState(candidate) {
    if (!candidate || candidate.version !== VERSION || !Array.isArray(candidate.items)) return freshState();
    if (Date.parse(candidate.expiresAt || '') < Date.now()) return freshState();
    const base = freshState();
    const contact = { ...base.contact, ...(candidate.contact || {}) };
    if (number(candidate.consentDefaultsVersion) < 1) {
      contact.processingConsent = true;
      contact.marketingConsent = true;
    }
    const project = { ...base.project, ...(candidate.project || {}) };
    const firstRoom = { ...base.project.rooms[0], ...(Array.isArray(project.rooms) ? project.rooms[0] : null) };
    if (firstRoom.name === 'Ambiente 1') firstRoom.name = 'Ambiente';
    project.rooms = [firstRoom];
    return {
      ...base,
      ...candidate,
      items: candidate.items.map((item) => ({ ...item, roomIds: [firstRoom.id] })),
      project,
      consentDefaultsVersion: 1,
      contact,
      services: candidate.services && typeof candidate.services === 'object' ? candidate.services : {},
      documents: Array.isArray(candidate.documents) ? candidate.documents : [],
      submission: { ...base.submission, ...(candidate.submission || {}) }
    };
  }

  function loadState() {
    try { return normalizeState(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')); }
    catch (_) { return freshState(); }
  }

  let state = loadState();

  function saveState({ preserveSubmission = false } = {}) {
    state.updatedAt = nowIso();
    state.expiresAt = new Date(Date.now() + MAX_AGE_MS).toISOString();
    if (!preserveSubmission && state.submission.status === 'failed') {
      state.submission.status = 'idle';
      state.submission.error = null;
    }
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    catch (_) { announce('No pudimos guardar el presupuesto en este dispositivo.'); }
    updateResume();
    document.dispatchEvent(new CustomEvent('project-quote:change', { detail: { state } }));
  }

  function announce(message) {
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 20);
  }

  function analytics(event, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    const payload = {
      event,
      quote_id: state.id,
      item_count: state.items.length,
      estimated_value: totals().knownMinor / 100,
      currency: state.currency,
      ...detail
    };
    delete payload.email;
    delete payload.phone;
    delete payload.fullName;
    window.dataLayer.push(payload);
  }

  function formatMoney(minor, currency = state.currency) {
    return new Intl.NumberFormat('es-UY', { style: 'currency', currency, maximumFractionDigits: 2 }).format(number(minor) / 100);
  }
  const pluralize = (value, singular, plural) => `${value} ${number(value) === 1 ? singular : plural}`;

  function calculateItem(item) {
    const inputMode = item.inputMode === 'sqm' ? 'sqm' : 'unit';
    const configuredWaste = item.wastePercent ?? item.defaultWastePercent;
    const waste = inputMode === 'sqm' ? normalizedWaste(configuredWaste) : 0;
    const area = inputMode === 'sqm' ? Math.max(0, number(item.areaSqm)) : null;
    const coverage = Math.max(0, number(item.coverageM2PerPackage));
    const requestedUnits = inputMode === 'unit' ? Math.max(1, Math.ceil(number(item.requestedUnits, 1))) : null;
    const effectiveArea = area == null ? null : round2(area * (1 + waste / 100));
    const calculatedQuantity = inputMode === 'sqm'
      ? coverage > 0 ? Math.ceil(effectiveArea / coverage) : round2(effectiveArea)
      : requestedUnits;
    const priceMinor = item.hiddenPrice || item.priceMinor == null ? null : Math.max(0, number(item.priceMinor));
    const subtotalMinor = priceMinor == null ? null : Math.round(priceMinor * calculatedQuantity);
    return { ...item, inputMode, wastePercent: waste, areaSqm: area, requestedUnits, effectiveArea, calculatedQuantity, subtotalMinor };
  }

  function totals() {
    const calculated = state.items.map(calculateItem);
    return {
      knownMinor: calculated.reduce((sum, item) => sum + (item.subtotalMinor ?? 0), 0),
      pending: calculated.some((item) => item.subtotalMinor == null) || Object.values(state.services).some((value) => value === 'advice' || value === 'add'),
      count: calculated.length
    };
  }

  function updateResume() {
    const resume = root.querySelector('[data-pq-resume]');
    const count = root.querySelector('[data-pq-resume-count]');
    if (!resume || !count) return;
    count.textContent = String(state.items.length);
    resume.hidden = state.items.length === 0 || Boolean(document.querySelector('[data-project-quote-builder]'));
  }

  function focusables(container) {
    return [...container.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null && !element.classList.contains('pq-backdrop'));
  }

  function openOverlay(element, trigger) {
    if (!element) return;
    if (activeOverlay && activeOverlay !== element) closeOverlay(activeOverlay, false);
    returnFocus = trigger || document.activeElement;
    activeOverlay = element;
    element.hidden = false;
    element.setAttribute('aria-hidden', 'false');
    if (element.id === 'ProjectQuoteConfirmation') root.querySelector('[data-pq-resume]')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('pq-scroll-lock');
    const dialog = element.querySelector('[role="dialog"]');
    requestAnimationFrame(() => (dialog?.querySelector('[data-pq-initial-focus]') || focusables(dialog || element)[0] || dialog)?.focus());
  }

  function closeOverlay(element = activeOverlay, restore = true) {
    if (!element) return;
    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    if (element.id === 'ProjectQuoteConfirmation') root.querySelector('[data-pq-resume]')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('pq-scroll-lock');
    if (element === activeOverlay) activeOverlay = null;
    if (restore && returnFocus?.focus) returnFocus.focus();
  }

  document.addEventListener('keydown', (event) => {
    if (!activeOverlay) return;
    if (event.key === 'Escape') { event.preventDefault(); closeOverlay(); return; }
    if (event.key !== 'Tab') return;
    const elements = focusables(activeOverlay);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  root.addEventListener('click', (event) => {
    const close = event.target.closest('[data-pq-close]');
    if (close) { event.preventDefault(); closeOverlay(close.closest('.pq-modal,.pq-confirm')); }
    const resume = event.target.closest('[data-pq-resume]');
    if (resume) { event.preventDefault(); renderConfirmation(null, resume); }
  });

  function productForButton(button) {
    const id = button.dataset.projectQuoteProduct;
    const script = id ? document.getElementById(id) : null;
    if (!script) throw new Error('No encontramos la configuración de este producto.');
    const product = JSON.parse(script.textContent);
    const wrapper = button.closest('.product-form-wrapper') || document;
    const selectedId = wrapper.querySelector('input[name="id"]')?.value;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = selectedId
      ? variants.find((entry) => String(entry.id) === String(selectedId))
      : variants.find(variantIsAvailable);
    requireAvailableVariant(variant);
    return { ...product, selectedVariant: variant };
  }

  function showAddDialog(product, trigger, item = null) {
    const variant = requireAvailableVariant(product.selectedVariant);
    activeProduct = product;
    editingItemId = item?.id || null;
    const dialog = document.getElementById('ProjectQuoteAddDialog');
    const form = document.getElementById('ProjectQuoteAddForm');
    dialog.querySelector('[data-pq-add-title]').textContent = product.title;
    dialog.querySelector('[data-pq-add-variant]').textContent = variant.title === 'Default Title' ? '' : variant.title;
    const image = dialog.querySelector('[data-pq-add-image]');
    image.src = variant.image || product.image || '';
    image.alt = product.title;
    const sqm = product.quantityMode === 'sqm';
    const hasPackageCoverage = number(product.coverageM2PerPackage) > 0;
    const defaultWaste = normalizedWaste(product.defaultWastePercent);
    dialog.querySelector('[data-pq-amount-label]').textContent = sqm ? 'Metros cuadrados' : 'Unidades';
    dialog.querySelector('[data-pq-amount-unit]').textContent = sqm ? 'm²' : 'unid.';
    dialog.querySelector('[data-pq-amount-help]').textContent = sqm
      ? hasPackageCoverage
        ? defaultWaste > 0
          ? 'Ingresá el área real; calcularemos el margen y las cajas necesarias.'
          : 'Ingresá el área real; calcularemos las cajas sin agregar un margen automático.'
        : 'Ingresá el área real; estimaremos los m² a cotizar. La modulación y el margen final se confirman con el proyecto.'
      : 'Ingresá la cantidad de unidades que necesitás.';
    dialog.querySelector('[data-pq-waste-field]').hidden = !sqm || defaultWaste === 0;
    form.amount.step = sqm ? '0.01' : '1';
    form.amount.value = item ? (sqm ? item.areaSqm : item.requestedUnits) : (sqm ? '' : '1');
    form.waste.value = defaultWaste;
    dialog.querySelector('[data-pq-add-submit] span').textContent = item ? 'Guardar cambios' : 'Agregar';
    dialog.querySelector('[data-pq-add-error]').hidden = true;
    updateAddCalculation();
    openOverlay(dialog, trigger);
  }

  function addDraftFromForm() {
    const form = document.getElementById('ProjectQuoteAddForm');
    const variant = requireAvailableVariant(activeProduct?.selectedVariant);
    const variants = Array.isArray(activeProduct.variants) && activeProduct.variants.length
      ? activeProduct.variants
      : [variant];
    return calculateItem({
      id: editingItemId || uid(),
      productId: String(activeProduct.productId),
      variantId: String(variant.id),
      productTitle: activeProduct.title,
      variantTitle: variant.title === 'Default Title' ? '' : variant.title,
      sku: variant.sku || '',
      image: variant.image || activeProduct.image || '',
      url: activeProduct.url || '',
      selectedOptions: Array.isArray(variant.options) ? variant.options : [],
      availableVariants: variants.map((entry) => ({
        id: String(entry.id), title: entry.title || '', sku: entry.sku || '', image: entry.image || '',
        options: Array.isArray(entry.options) ? entry.options : [],
        available: entry.available !== false,
        priceMinor: activeProduct.hiddenPrice ? null : entry.priceMinor ?? null
      })),
      inputMode: activeProduct.quantityMode,
      areaSqm: activeProduct.quantityMode === 'sqm' ? number(form.amount.value) : null,
      requestedUnits: activeProduct.quantityMode === 'unit' ? number(form.amount.value) : null,
      wastePercent: activeProduct.quantityMode === 'sqm' ? normalizedWaste(activeProduct.defaultWastePercent) : 0,
      coverageM2PerPackage: number(activeProduct.coverageM2PerPackage),
      priceMinor: activeProduct.hiddenPrice ? null : variant.priceMinor ?? null,
      hiddenPrice: Boolean(activeProduct.hiddenPrice),
      currency: activeProduct.currency || defaultCurrency,
      serviceCodes: Array.isArray(activeProduct.serviceCodes) ? activeProduct.serviceCodes : [],
      tags: Array.isArray(activeProduct.tags) ? activeProduct.tags : [],
      roomIds: [state.project.rooms[0].id],
      addedAt: nowIso()
    });
  }

  function updateAddCalculation() {
    if (!activeProduct) return;
    const form = document.getElementById('ProjectQuoteAddForm');
    const output = document.querySelector('[data-pq-calculation]');
    const amount = number(form.amount.value);
    if (!(amount > 0)) { output.textContent = 'Completá la cantidad para ver el cálculo.'; return; }
    let item;
    try { item = addDraftFromForm(); }
    catch (error) { output.textContent = error.message; return; }
    if (item.inputMode === 'sqm') {
      const quantityText = item.coverageM2PerPackage > 0
        ? `${item.calculatedQuantity} caja${item.calculatedQuantity === 1 ? '' : 's'}`
        : `${item.effectiveArea} m² estimados para cotizar`;
      output.textContent = item.wastePercent > 0
        ? `${item.areaSqm} m² + ${item.wastePercent}% = ${item.effectiveArea} m². Cantidad estimada: ${quantityText}.`
        : `${item.areaSqm} m² ingresados. El margen final se confirma según modulación, paños y juntas.`;
    } else output.textContent = `Cantidad solicitada: ${item.calculatedQuantity} unidad${item.calculatedQuantity === 1 ? '' : 'es'}.`;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-project-quote-open]');
    if (!button) return;
    event.preventDefault();
    try { showAddDialog(productForButton(button), button); }
    catch (error) { announce(error.message); }
  });

  const addForm = document.getElementById('ProjectQuoteAddForm');
  addForm?.addEventListener('input', updateAddCalculation);
  addForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    const error = document.querySelector('[data-pq-add-error]');
    let item;
    try { item = addDraftFromForm(); }
    catch (variantError) {
      error.textContent = variantError.message;
      error.hidden = false;
      return;
    }
    const valid = item.inputMode === 'sqm' ? item.areaSqm > 0 : item.requestedUnits > 0;
    if (!valid) {
      error.textContent = 'Revisá la cantidad.';
      error.hidden = false;
      return;
    }
    const previousIndex = state.items.findIndex((entry) => entry.id === item.id);
    if (previousIndex >= 0) state.items.splice(previousIndex, 1, item);
    else state.items.push(item);
    state.currency = item.currency || state.currency;
    saveState();
    closeOverlay(document.getElementById('ProjectQuoteAddDialog'), false);
    renderConfirmation(item.id);
    analytics(previousIndex >= 0 ? 'quote_item_updated' : 'quote_item_added', { product_id: item.productId, variant_id: item.variantId });
    if (state.items.length === 1 && previousIndex < 0) analytics('quote_started', { product_id: item.productId, variant_id: item.variantId });
    announce(`${item.productTitle} fue agregado al presupuesto.`);
  });

  function lineMarkup(item) {
    const calculated = calculateItem(item);
    const marginLabel = calculated.wastePercent > 0 ? `${calculated.wastePercent}% desperdicio` : 'margen a confirmar';
    const quantity = calculated.inputMode === 'sqm'
      ? calculated.coverageM2PerPackage === 0 && calculated.wastePercent === 0
        ? `${calculated.areaSqm} m² · ${marginLabel}`
        : `${calculated.areaSqm} m² · ${marginLabel} · ${calculated.coverageM2PerPackage > 0 ? pluralize(calculated.calculatedQuantity, 'caja', 'cajas') : `${calculated.calculatedQuantity} m²`}`
      : pluralize(calculated.calculatedQuantity, 'unidad', 'unidades');
    return `<article class="pq-line" data-pq-line="${escapeHtml(item.id)}">
      <img src="${escapeHtml(item.image || '')}" alt="" width="72" height="72" loading="lazy">
      <div><h3>${escapeHtml(item.productTitle)}</h3>${item.variantTitle ? `<p>${escapeHtml(item.variantTitle)}</p>` : ''}<p>${escapeHtml(quantity)}</p>
        <div class="pq-line-tools"><button type="button" class="pq-text-button" data-pq-edit="${escapeHtml(item.id)}">Editar</button><button type="button" class="pq-text-button" data-pq-remove="${escapeHtml(item.id)}">Quitar</button></div>
      </div>
      <div class="pq-line-total">${calculated.subtotalMinor == null ? 'Precio a confirmar' : formatMoney(calculated.subtotalMinor, item.currency)}</div>
    </article>`;
  }

  function renderConfirmation(lastItemId, trigger = null) {
    const overlay = document.getElementById('ProjectQuoteConfirmation');
    const items = state.items.slice();
    if (lastItemId) items.sort((a, b) => a.id === lastItemId ? 1 : b.id === lastItemId ? -1 : 0);
    overlay.querySelector('[data-pq-confirm-items]').innerHTML = items.map(lineMarkup).join('') || '<p class="pq-empty">Todavía no agregaste productos.</p>';
    overlay.querySelector('[data-pq-item-count]').textContent = String(state.items.length);
    overlay.querySelector('[data-pq-item-label]').textContent = state.items.length === 1 ? 'producto' : 'productos';
    overlay.querySelector('[data-pq-confirm-kicker]').textContent = lastItemId ? 'Producto agregado' : 'Tu selección';
    openOverlay(overlay, trigger || returnFocus);
  }

  root.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-pq-remove]');
    if (remove) {
      const id = remove.dataset.pqRemove;
      const item = state.items.find((entry) => entry.id === id);
      state.items = state.items.filter((entry) => entry.id !== id);
      saveState();
      analytics('quote_item_removed', { product_id: item?.productId, variant_id: item?.variantId });
      if (state.items.length) renderConfirmation(); else closeOverlay();
      announce('Producto quitado del presupuesto.');
      return;
    }
    const edit = event.target.closest('[data-pq-edit]');
    if (edit) {
      const item = state.items.find((entry) => entry.id === edit.dataset.pqEdit);
      if (!item) return;
      const storedVariants = Array.isArray(item.availableVariants) ? item.availableVariants : [];
      const storedSelectedVariant = storedVariants.find((variant) => String(variant.id) === String(item.variantId));
      const product = {
        productId: item.productId, title: item.productTitle, url: item.url, image: item.image,
        hiddenPrice: item.hiddenPrice, quantityMode: item.inputMode,
        coverageM2PerPackage: item.coverageM2PerPackage, defaultWastePercent: item.wastePercent,
        currency: item.currency, serviceCodes: item.serviceCodes, tags: item.tags,
        variants: storedVariants,
        selectedVariant: storedSelectedVariant || {
          id: item.variantId, title: item.variantTitle, sku: item.sku, image: item.image,
          options: item.selectedOptions, priceMinor: item.priceMinor,
          available: storedVariants.length === 0
        }
      };
      try { showAddDialog(product, edit, item); }
      catch (error) { announce(error.message); }
    }
  });

  function updateState(mutator) { mutator(state); saveState(); }

  function builderController() {
    const builder = document.querySelector('[data-project-quote-builder]');
    if (!builder) return;
    analytics('quote_builder_viewed');
    let currentStep = Math.min(3, Math.max(1, number(builder.dataset.initialStep, 1)));
    const stepButtons = [...builder.querySelectorAll('[data-pq-step-button]')];
    const stages = [...builder.querySelectorAll('[data-pq-stage]')];
    const servicesConfig = (() => {
      try { return JSON.parse(builder.querySelector('[data-pq-services-config]')?.textContent || '[]'); }
      catch (_) { return []; }
    })();
    servicesConfig.forEach((service) => {
      if (service.enabled === false || (service.onlyWithoutInstallation && state.services.installation !== 'decline')) {
        delete state.services[service.id];
      }
    });

    function showStep(step, track = false) {
      currentStep = Math.min(3, Math.max(1, step));
      stages.forEach((stage) => { stage.hidden = number(stage.dataset.pqStage) !== currentStep; });
      let activeButton = null;
      stepButtons.forEach((button) => {
        const buttonStep = number(button.dataset.pqStepButton);
        const active = buttonStep === currentStep;
        const complete = buttonStep < currentStep;
        if (active) activeButton = button;
        if (active) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
        button.classList.toggle('is-complete', complete);
        button.dataset.pqStepState = active ? 'current' : complete ? 'complete' : 'upcoming';
      });
      const stepNav = builder.querySelector('.pq-step-nav');
      if (stepNav && activeButton && stepNav.scrollWidth > stepNav.clientWidth) {
        requestAnimationFrame(() => {
          const navRect = stepNav.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();
          if (buttonRect.left < navRect.left || buttonRect.right > navRect.right) {
            stepNav.scrollBy({
              left: buttonRect.left - navRect.left - ((stepNav.clientWidth - buttonRect.width) / 2),
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
            });
          }
        });
      }
      builder.querySelector('[data-pq-mobile-action]').textContent = currentStep === 3 ? 'Enviar solicitud' : 'Continuar';
      if (track) analytics('quote_step_completed', { step: currentStep - 1 });
      window.scrollTo({ top: Math.max(0, builder.offsetTop - 20), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    function renderMaterials() {
      const list = builder.querySelector('[data-pq-materials]');
      if (!state.items.length) {
        list.innerHTML = `<div class="pq-empty">Todavía no agregaste materiales.<br><a href="/collections/all">Seguir viendo productos</a></div>`;
        return;
      }
      list.innerHTML = state.items.map((raw) => {
        const item = calculateItem(raw);
        const amount = item.inputMode === 'sqm' ? item.areaSqm : item.requestedUnits;
        const fieldId = `PqMaterial-${String(item.id).replace(/[^A-Za-z0-9_-]/g, '')}`;
        const availableVariants = Array.isArray(item.availableVariants) ? item.availableVariants : [];
        const selectedVariant = availableVariants.find((variant) => String(variant.id) === String(item.variantId));
        const selectedVariantUnavailable = availableVariants.length > 0 && !variantIsAvailable(selectedVariant);
        const hasVariants = availableVariants.length > 1 || selectedVariantUnavailable;
        return `<article class="pq-material-card" data-material-id="${escapeHtml(item.id)}">
          <img src="${escapeHtml(item.image || '')}" alt="" width="88" height="88" loading="lazy">
          <div><h3>${escapeHtml(item.productTitle)}</h3><p class="pq-muted">${escapeHtml(item.variantTitle)}</p><p>${item.subtotalMinor == null ? 'Precio a confirmar' : formatMoney(item.subtotalMinor,item.currency)}</p></div>
          <div class="pq-material-fields${hasVariants ? ' pq-material-fields--variants' : ''}">
            ${hasVariants ? `<div class="pq-field"><label for="${fieldId}-Variant">Variante</label><select id="${fieldId}-Variant" data-material-variant ${selectedVariantUnavailable ? 'aria-invalid="true"' : ''}>${!selectedVariant ? '<option value="" selected disabled>Variante no disponible</option>' : ''}${availableVariants.map((variant) => `<option value="${escapeHtml(variant.id)}" ${String(variant.id) === String(item.variantId) ? 'selected' : ''} ${variantIsAvailable(variant) ? '' : 'disabled'}>${escapeHtml(variant.title || 'Variante')}${variantIsAvailable(variant) ? '' : ' — No disponible'}</option>`).join('')}</select>${selectedVariantUnavailable ? '<p class="pq-error">Elegí una variante disponible para continuar.</p>' : ''}</div>` : ''}
            <div class="pq-field"><label for="${fieldId}-Amount">Cantidad (${item.inputMode === 'sqm' ? 'm²' : 'unidades'})</label><input id="${fieldId}-Amount" data-material-amount type="number" min="0.01" max="100000" step="${item.inputMode === 'sqm' ? '0.01' : '1'}" value="${amount ?? ''}"></div>
            ${item.inputMode === 'sqm' && item.wastePercent > 0 ? `<div class="pq-field pq-material-waste-field"><label for="${fieldId}-Waste">Margen estimado</label><div class="pq-input-suffix pq-waste-input"><input id="${fieldId}-Waste" data-material-waste type="number" value="${item.wastePercent}" disabled aria-disabled="true"><span>%</span></div></div>` : ''}
          </div>
          <div class="pq-line-tools"><button type="button" class="pq-text-button" data-material-remove>Quitar producto</button></div>
        </article>`;
      }).join('');
    }

    function relevantServices() {
      const itemSignals = state.items.flatMap((item) => [...(item.serviceCodes || []), ...(item.tags || [])]).map((value) => String(value).toLowerCase());
      const includesHomogeneousRoll = itemSignals.includes('homogeneo');
      return servicesConfig.filter((service) => {
        if (service.enabled === false) return false;
        if (includesHomogeneousRoll && service.id === 'baseboards') return false;
        if (service.onlyWithoutInstallation && state.services.installation !== 'decline') return false;
        if (service.alwaysSuggest) return true;
        const signals = (service.matchSignals || []).map((value) => String(value).toLowerCase());
        return signals.length === 0 || signals.some((signal) => itemSignals.includes(signal));
      });
    }

    function renderServices() {
      const list = builder.querySelector('[data-pq-services]');
      const services = relevantServices();
      if (!services.length) { list.innerHTML = '<p class="pq-empty">No hay sugerencias configuradas para estos productos.</p>'; return; }
      list.innerHTML = services.map((service) => {
        const decision = state.services[service.id] || 'undecided';
        return `<article class="pq-service-card" data-service-id="${escapeHtml(service.id)}">
          <div><h3>${escapeHtml(service.label)}</h3>${service.description ? `<p class="pq-muted">${escapeHtml(service.description)}</p>` : ''}</div>
          <div class="pq-segmented" role="radiogroup" aria-label="Decisión sobre ${escapeHtml(service.label)}">
            ${[['add','Agregar'],['decline','No lo necesito'],['advice','Necesito asesoramiento']].map(([value,label]) => `<label><input type="radio" name="service-${escapeHtml(service.id)}" value="${value}" ${decision === value ? 'checked' : ''}><span>${label}</span></label>`).join('')}
          </div>
        </article>`;
      }).join('');
    }

    function summaryMarkup() {
      const total = totals();
      const chosenServices = relevantServices().filter((service) => ['add','advice'].includes(state.services[service.id]));
      return `<div class="pq-summary-row"><span>Materiales</span><strong>${state.items.length}</strong></div>
        <div class="pq-summary-row"><span>Servicios</span><strong>${chosenServices.length}</strong></div>
        <div class="pq-summary-row pq-summary-total"><span>Estimación</span><strong>${total.pending ? (total.knownMinor > 0 ? `${formatMoney(total.knownMinor)} + pendientes` : 'Precio a confirmar') : formatMoney(total.knownMinor)}</strong></div>`;
    }

    function renderSummary() {
      const markup = summaryMarkup();
      builder.querySelector('[data-pq-summary-body]').innerHTML = markup;
      builder.querySelector('[data-pq-summary-mobile]').innerHTML = markup;
      const total = totals();
      builder.querySelector('[data-pq-mobile-total]').textContent = total.pending ? 'Precio a confirmar' : formatMoney(total.knownMinor);
    }

    function materialPreviewMarkup() {
      if (!state.items.length) return '';
      const visibleItemCount = 2;
      const items = state.items.map(calculateItem);
      const lines = items.map((item, index) => {
        const margin = item.wastePercent > 0 ? `+ ${item.wastePercent}%` : '· margen a confirmar';
        const quantity = item.inputMode === 'sqm'
          ? item.coverageM2PerPackage > 0
            ? `${item.areaSqm} m² ${margin} · ${pluralize(item.calculatedQuantity, 'caja', 'cajas')}`
            : item.wastePercent === 0
              ? `${item.areaSqm} m² ${margin}`
              : `${item.areaSqm} m² ${margin} · ${item.effectiveArea} m² estimados`
          : pluralize(item.calculatedQuantity, 'unidad', 'unidades');
        const price = item.subtotalMinor == null ? 'Precio a confirmar' : formatMoney(item.subtotalMinor, item.currency);
        return `<article class="pq-material-preview-item${index >= visibleItemCount ? ' pq-material-preview-extra' : ''}"${index >= visibleItemCount ? ' hidden' : ''}>
          <img src="${escapeHtml(item.image || '')}" alt="" width="58" height="58" loading="lazy">
          <div><h4>${escapeHtml(item.productTitle)}</h4><p>${escapeHtml(quantity)}</p><p class="pq-material-preview-price">${escapeHtml(price)}</p></div>
        </article>`;
      }).join('');
      const remaining = Math.max(0, state.items.length - visibleItemCount);
      return `<section class="pq-material-preview-card" aria-label="Materiales seleccionados">
        <div class="pq-material-preview-head"><h3>Materiales seleccionados</h3><button type="button" class="pq-text-button" data-pq-go-materials>Revisar</button></div>
        <div class="pq-material-preview-list">${lines}</div>
        ${remaining > 0 ? `<button type="button" class="pq-material-preview-more" data-pq-preview-toggle aria-expanded="false" data-collapsed-label="+ ${remaining} material${remaining === 1 ? '' : 'es'} más">+ ${remaining} material${remaining === 1 ? '' : 'es'} más</button>` : ''}
      </section>`;
    }

    function renderMaterialPreviews() {
      const markup = materialPreviewMarkup();
      builder.querySelectorAll('[data-pq-material-preview]').forEach((container) => {
        container.hidden = !markup;
        container.innerHTML = markup;
      });
    }

    function reviewMarkup() {
      const materials = state.items.map(calculateItem);
      const services = relevantServices().filter((service) => ['add','advice'].includes(state.services[service.id]));
      return `<section class="pq-review-group"><h3>Tu proyecto</h3><p>${escapeHtml(state.project.name || 'Proyecto sin nombre')} · ${state.project.kind === 'commercial' ? 'Comercial' : 'Residencial'} · ${state.project.workType === 'new' ? 'Obra nueva' : 'Renovación'}</p><p>${escapeHtml(state.project.location || 'Ubicación pendiente')}</p>${state.project.desiredDate ? `<p><strong>Fecha deseada:</strong> ${escapeHtml(displayDate(state.project.desiredDate))}</p>` : ''}${state.project.notes ? `<p class="pq-review-notes"><strong>Notas:</strong> ${escapeHtml(state.project.notes)}</p>` : ''}</section>
        <section class="pq-review-group"><h3>Materiales</h3><ul>${materials.map((item) => {
          const margin = item.wastePercent > 0 ? `+ ${item.wastePercent}%` : '· margen a confirmar';
          const quantity = item.inputMode === 'sqm'
            ? item.coverageM2PerPackage > 0
              ? `${item.areaSqm} m² ${margin} (${pluralize(item.calculatedQuantity, 'caja', 'cajas')})`
              : item.wastePercent === 0
                ? `${item.areaSqm} m² ${margin}`
                : `${item.areaSqm} m² ${margin} (${item.effectiveArea} m² estimados)`
            : pluralize(item.calculatedQuantity, 'unidad', 'unidades');
          return `<li><strong>${escapeHtml(item.productTitle)}</strong>: ${quantity} — ${item.subtotalMinor == null ? 'Precio a confirmar' : formatMoney(item.subtotalMinor,item.currency)}</li>`;
        }).join('') || '<li>Sin materiales</li>'}</ul></section>
        <section class="pq-review-group"><h3>Servicios y complementos</h3><ul>${services.map((service) => `<li>${escapeHtml(service.label)} — ${state.services[service.id] === 'advice' ? 'Necesito asesoramiento' : 'Agregar'}</li>`).join('') || '<li>Sin servicios seleccionados</li>'}</ul></section>
        <p class="pq-disclaimer">Esta es una estimación preliminar basada en la información ingresada. Las cantidades, condiciones de instalación y precios finales serán confirmados por un asesor de Mr. Parquet.</p>`;
    }

    function renderReview() { builder.querySelector('[data-pq-review]').innerHTML = reviewMarkup(); }

    function renderAll() { renderMaterials(); renderServices(); renderSummary(); renderMaterialPreviews(); renderReview(); }

    function readProjectFields() {
      const field = (name) => builder.querySelector(`[name="project_${name}"]`)?.value || '';
      state.project.name = field('name').slice(0,150);
      state.project.kind = field('kind') || 'residential';
      state.project.workType = field('work_type') || 'renovation';
      state.project.location = field('location').slice(0,300);
      const desiredDate = field('desired_date').trim();
      const parsedDesiredDate = desiredDate ? parseDisplayDate(desiredDate) : '';
      state.project.desiredDate = desiredDate ? (parsedDesiredDate || desiredDate) : '';
      const dateError = builder.querySelector('[data-pq-date-error]');
      if (dateError) dateError.hidden = !desiredDate || Boolean(parsedDesiredDate);
      const dateInput = builder.querySelector('[name="project_desired_date"]');
      if (dateInput) dateInput.setAttribute('aria-invalid', desiredDate && !parsedDesiredDate ? 'true' : 'false');
      state.project.notes = field('notes').slice(0,5000);
      saveState(); renderSummary(); renderReview();
    }

    ['name','kind','work_type','location','desired_date','notes'].forEach((name) => {
      const element = builder.querySelector(`[name="project_${name}"]`);
      if (!element) return;
      const key = name === 'work_type' ? 'workType' : name === 'desired_date' ? 'desiredDate' : name;
      element.value = name === 'desired_date' ? displayDate(state.project[key]) : (state.project[key] ?? '');
      element.addEventListener('change', readProjectFields);
      if (name === 'desired_date') element.addEventListener('input', () => { element.value = formatDateTyping(element.value); });
      if (['name','location','notes'].includes(name)) element.addEventListener('input', readProjectFields);
    });

    builder.addEventListener('click', (event) => {
      const step = event.target.closest('[data-pq-step-button]');
      if (step) { showStep(number(step.dataset.pqStepButton)); return; }
      const previewToggle = event.target.closest('[data-pq-preview-toggle]');
      if (previewToggle) {
        const card = previewToggle.closest('.pq-material-preview-card');
        const expanded = previewToggle.getAttribute('aria-expanded') === 'true';
        card.querySelectorAll('.pq-material-preview-extra').forEach((item) => { item.hidden = expanded; });
        previewToggle.setAttribute('aria-expanded', String(!expanded));
        previewToggle.textContent = expanded ? previewToggle.dataset.collapsedLabel : 'Ver menos';
        return;
      }
      if (event.target.closest('[data-pq-go-materials]')) { showStep(2); return; }
      if (event.target.closest('[data-pq-next]')) {
        saveState(); renderSummary(); renderReview();
        showStep(currentStep + 1, true); return;
      }
      if (event.target.closest('[data-pq-prev]')) { showStep(currentStep - 1); return; }
      const materialRemove = event.target.closest('[data-material-remove]');
      if (materialRemove) {
        const card = materialRemove.closest('[data-material-id]');
        const item = state.items.find((entry) => entry.id === card.dataset.materialId);
        state.items = state.items.filter((entry) => entry.id !== card.dataset.materialId);
        saveState(); analytics('quote_item_removed', { product_id: item?.productId, variant_id: item?.variantId }); renderAll();
      }
    });

    builder.addEventListener('change', (event) => {
      const material = event.target.closest('[data-material-id]');
      if (material) {
        const item = state.items.find((entry) => entry.id === material.dataset.materialId);
        if (!item) return;
        const variantId = material.querySelector('[data-material-variant]')?.value;
        const variant = variantId ? (item.availableVariants || []).find((entry) => String(entry.id) === String(variantId)) : null;
        if (variant && variantIsAvailable(variant)) {
          item.variantId = String(variant.id);
          item.variantTitle = variant.title === 'Default Title' ? '' : variant.title;
          item.sku = variant.sku || '';
          item.image = variant.image || item.image;
          item.selectedOptions = variant.options || [];
          item.priceMinor = item.hiddenPrice ? null : variant.priceMinor ?? null;
        }
        if (!storedItemVariantIsAvailable(item)) {
          announce('La variante seleccionada no está disponible. Elegí otra opción.');
          renderMaterials();
          return;
        }
        const amount = number(material.querySelector('[data-material-amount]')?.value);
        if (item.inputMode === 'sqm') item.areaSqm = amount;
        else item.requestedUnits = amount;
        item.roomIds = [state.project.rooms[0].id];
        Object.assign(item, calculateItem(item)); saveState(); renderMaterials(); renderSummary(); renderMaterialPreviews(); renderReview();
        return;
      }
      const service = event.target.closest('[data-service-id]');
      if (service && event.target.matches('input[type="radio"]')) {
        const serviceId = service.dataset.serviceId;
        state.services[serviceId] = event.target.value;
        if (serviceId === 'installation' && event.target.value !== 'decline') delete state.services.delivery;
        saveState(); renderServices(); renderSummary(); renderReview();
      }
    });

    const contactMap = { fullName:'full_name', email:'email', phone:'phone', company:'company', preferredChannel:'preferred_channel', comments:'comments', processingConsent:'processing_consent', marketingConsent:'marketing_consent' };
    Object.entries(contactMap).forEach(([key,name]) => {
      const input = builder.querySelector(`[name="${name}"]`);
      if (!input) return;
      if (input.type === 'checkbox') input.checked = Boolean(state.contact[key]); else input.value = state.contact[key] || '';
      input.addEventListener('change', () => { state.contact[key] = input.type === 'checkbox' ? input.checked : input.value.slice(0,5000); saveState(); });
      if (input.type !== 'checkbox') input.addEventListener('input', () => { state.contact[key] = input.value.slice(0,5000); saveState(); });
    });

    function validateSubmission() {
      if (!state.items.length) return 'Agregá al menos un producto.';
      const unavailableItem = state.items.find((item) => !storedItemVariantIsAvailable(item));
      if (unavailableItem) return `La variante seleccionada de ${unavailableItem.productTitle || 'un producto'} no está disponible. Elegí otra opción.`;
      if (state.project.desiredDate && !/^\d{4}-\d{2}-\d{2}$/.test(state.project.desiredDate)) return 'Ingresá la fecha deseada en formato dd/mm/aaaa.';
      if (!state.contact.fullName.trim()) return 'Ingresá tu nombre completo.';
      if (!/^\S+@\S+\.\S+$/.test(state.contact.email)) return 'Ingresá un email válido.';
      if (!state.contact.phone.trim()) return 'Ingresá un teléfono o WhatsApp.';
      if (!state.contact.processingConsent) return 'Necesitamos tu autorización para procesar el presupuesto.';
      return null;
    }

    function submissionPayload() {
      const selectedServices = relevantServices().map((service) => ({
        id: service.id, label: service.label, decision: state.services[service.id] || 'undecided'
      })).filter((service) => service.decision !== 'undecided');
      return {
        schemaVersion: 1,
        idempotencyKey: state.submission.idempotencyKey,
        source: 'shopify_project_quote_builder',
        shopDomain,
        currency: state.currency,
        customer: {
          fullName: state.contact.fullName, email: state.contact.email, phone: state.contact.phone,
          company: state.contact.company || null, preferredChannel: state.contact.preferredChannel,
          comments: state.contact.comments || null
        },
        project: {
          name: state.project.name || null, kind: state.project.kind, workType: state.project.workType,
          location: state.project.location, desiredDate: state.project.desiredDate || null,
          notes: state.project.notes || null,
          rooms: state.project.rooms.map((room) => ({ id: room.id, name: room.name, areaSqm: number(room.areaSqm) }))
        },
        products: state.items.map((raw) => {
          const item = calculateItem(raw);
          return {
            clientItemId: item.id, productId: item.productId, variantId: item.variantId,
            title: item.productTitle, variantTitle: item.variantTitle || null, sku: item.sku || null,
            selectedOptions: item.selectedOptions, inputMode: item.inputMode,
            areaSqm: item.areaSqm, requestedUnits: item.requestedUnits,
            wastePercent: item.wastePercent, coverageM2PerPackage: item.coverageM2PerPackage,
            calculatedQuantity: item.calculatedQuantity, roomIds: item.roomIds || [], hiddenPrice: item.hiddenPrice
          };
        }),
        services: selectedServices,
        documents: [],
        attribution: { ...state.attribution, currentPage: window.location.href.slice(0,1000) },
        consents: {
          quoteProcessing: { granted: true, recordedAt: nowIso() },
          marketing: { granted: Boolean(state.contact.marketingConsent), recordedAt: nowIso() }
        },
        createdAt: state.createdAt,
        submittedAt: nowIso(),
        honeypot: ''
      };
    }

    async function submit() {
      const message = builder.querySelector('[data-pq-submit-error]');
      const validation = validateSubmission();
      if (validation) { message.textContent = validation; message.hidden = false; showStep(validation.includes('nombre') || validation.includes('email') || validation.includes('teléfono') || validation.includes('autorización') ? 3 : 2); return; }
      message.hidden = true;
      const button = builder.querySelector('[data-pq-submit]');
      button.disabled = true;
      button.setAttribute('aria-busy','true');
      state.submission.status = 'submitting'; saveState({ preserveSubmission: true });
      try {
        const response = await fetch(builder.dataset.submitEndpoint || '/apps/quoteflow/project-quotes', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(submissionPayload())
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success || !data.reference) throw new Error(data.error || 'No pudimos enviar la solicitud.');
        state.submission = { status: 'confirmed', idempotencyKey: state.submission.idempotencyKey, reference: data.reference, error: null };
        saveState({ preserveSubmission: true });
        analytics('quote_submitted', { reference_present: true });
        builder.querySelector('[data-pq-builder-workflow]').hidden = true;
        const success = builder.querySelector('[data-pq-success]');
        success.hidden = false;
        success.querySelector('[data-pq-reference]').textContent = data.reference;
        localStorage.removeItem(STORAGE_KEY);
        state = freshState();
        updateResume();
        success.focus();
      } catch (error) {
        state.submission.status = 'failed'; state.submission.error = error.message; saveState({ preserveSubmission: true });
        message.textContent = `${error.message} Tu presupuesto sigue guardado en este dispositivo.`; message.hidden = false;
        analytics('quote_submission_failed', { error_type: 'request_failed' });
      } finally { button.disabled = false; button.removeAttribute('aria-busy'); }
    }

    builder.querySelector('[data-pq-submit]')?.addEventListener('click', submit);
    builder.querySelector('[data-pq-mobile-action]')?.addEventListener('click', () => {
      if (currentStep === 3) { submit(); return; }
      saveState(); renderSummary(); renderReview();
      showStep(currentStep + 1, true);
    });
    renderAll(); showStep(currentStep);
  }

  window.ProjectQuote = {
    getState: () => JSON.parse(JSON.stringify(state)),
    open: () => renderConfirmation(),
    clear: () => { localStorage.removeItem(STORAGE_KEY); state = freshState(); updateResume(); }
  };

  updateResume();
  builderController();
})();
